import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { ChevronDownIcon } from "lucide-react";
import { cn } from "../utils";

type AccordionVariant = "default" | "outlined" | "filled";

const AccordionContext = React.createContext<{
  variant: AccordionVariant;
}>({
  variant: "default",
});

function Accordion({
  variant = "default",
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root> & {
  variant?: AccordionVariant;
}) {
  return (
    <AccordionContext.Provider value={{ variant }}>
      <AccordionPrimitive.Root data-slot="accordion" {...props} />
    </AccordionContext.Provider>
  );
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  const { variant } = React.useContext(AccordionContext);

  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        "qodo qodo-accordion-item",
        "text-text-interactive-neutral-idle text-base font-medium leading-6",
        "hover:text-text-interactive-neutral-hover",
        {
          "border-b border-neutral-600": variant === "default",
          "border border-neutral-600 mb-2 px-4 rounded-lg":
            variant === "outlined",
          "bg-fill-interactive-neutral-idle mb-2 px-2 rounded-lg":
            variant === "filled",
        },
        className,
      )}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  iconPosition = "right",
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  iconPosition?: "left" | "right";
}) {
  const { variant } = React.useContext(AccordionContext);

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "qodo qodo-accordion-trigger",
          "flex flex-1 items-start justify-between gap-4 rounded-md",
          "text-left text-sm font-medium transition-all outline-none",
          "text-text-interactive-neutral-idle cursor-pointer",
          "hover:text-text-interactive-neutral-hover",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "disabled:pointer-events-none disabled:opacity-50",
          "[&[data-state=open]>svg]:rotate-180",
          {
            "py-4": variant !== "filled",
            "py-2": variant === "filled",
            "flex-row-reverse justify-end gap-2": iconPosition === "left",
          },
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        "qodo qodo-accordion-content",
        "overflow-hidden text-sm text-foreground",
        "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      )}
      {...props}
    >
      <div
        className={cn(
          "pt-0 pb-4",
          "text-text-interactive-neutral-idle font-normal text-sm",
          className,
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
